import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { SubscriptionCard, SubscriptionStats } from './components';
import { useToast } from '@/components/ui/toast-provider';

import { useListUserSubscriptionsQuery } from '@/services/api/services/subscriptionsApi';
import { useCancelSubscriptionMutation } from '@/services/api/services/servicesApi';
import type { Subscription, SubscriptionStats as ISubscriptionStats, GroupedSubscriptionsByService } from '@/types/subscriptions';
import { ROUTES } from '@/utils/constants';

const MySubscriptions: React.FC = () => {
  const navigate = useNavigate();
  const { data, error: apiError, isLoading, refetch } = useListUserSubscriptionsQuery({});
  const [cancelSubscription] = useCancelSubscriptionMutation();
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const { showToast } = useToast();

  const error = apiError ? 'Failed to load subscriptions' : null;

  const subscriptions: Subscription[] = data?.subscriptions ?? [];

  // Group subscriptions by service
  const groupedSubscriptions: GroupedSubscriptionsByService = subscriptions.reduce((acc, subscription) => {
    const serviceId = subscription.service.id;
    if (!acc[serviceId]) {
      acc[serviceId] = {
        service: subscription.service,
        subscriptions: []
      };
    }
    acc[serviceId].subscriptions.push(subscription);
    return acc;
  }, {} as GroupedSubscriptionsByService);

  // Calculate stats from subscriptions data
  const stats: ISubscriptionStats | null = data
    ? {
        active_subscriptions: subscriptions.filter(sub => sub.status === 'active').length,
        active_services: new Set(
          subscriptions.filter(sub => sub.status === 'active').map(sub => sub.service.id),
        ).size,
        total_monthly_cost: subscriptions
          .filter(sub => sub.status === 'active')
          .reduce((sum, sub) => sum + (sub.plan.amount_cents / 100), 0),
      }
    : null;

  const activeServiceGroups = Object.values(groupedSubscriptions).filter(group => 
    group.subscriptions.some(sub => sub.status === 'active')
  );
  const expiredServiceGroups = Object.values(groupedSubscriptions).filter(group => 
    group.subscriptions.every(sub => sub.status !== 'active') && group.subscriptions.length > 0
  );

  const handleCancelSubscription = async (id: string): Promise<void> => {
    const isConfirmed = window.confirm('Are you sure you want to cancel this subscription?');
    if (!isConfirmed) {
      return;
    }
    setCancellingId(id);
    try {
      await cancelSubscription(id).unwrap();
      refetch();
      
      // Show success toast
      showToast({
        type: 'success',
        title: 'Subscription cancelled successfully!',
        message: 'Your subscription has been cancelled and will expire at the end of the current billing period.',
      });
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      
      // Show error toast
      showToast({
        type: 'error',
        title: 'Cancellation failed',
        message: 'Failed to cancel subscription. Please try again or contact support.',
      });
    } finally {
      setCancellingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" text="Loading subscriptions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-bold text-gray-900">My Subscriptions</h1>
        <p className="text-lg text-gray-600">
          Manage your active and past service subscriptions.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && <SubscriptionStats stats={stats} />}

      {/* Active Subscriptions Grouped by Service */}
      <div className="space-y-8">
        <h2 className="text-2xl font-semibold text-gray-900">Active Subscriptions</h2>
        {activeServiceGroups.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No active subscriptions found.</p>
          </div>
        ) : (
          activeServiceGroups.map((serviceGroup) => {
            const activeSubscriptions = serviceGroup.subscriptions.filter(sub => sub.status === 'active');
            return (
              <div key={serviceGroup.service.id} className="space-y-4">
                {/* Service Header */}
                <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                  {serviceGroup.service.icon_url && (
                    <img 
                      src={serviceGroup.service.icon_url} 
                      alt={`${serviceGroup.service.name} icon`}
                      className="w-8 h-8 rounded"
                    />
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{serviceGroup.service.name}</h3>
                    <p className="text-sm text-gray-600">{serviceGroup.service.description}</p>
                  </div>
                </div>
                
                {/* Service Subscriptions */}
                <div className="space-y-4 pl-4">
                  {activeSubscriptions.map((subscription) => (
                    <SubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      onViewUsage={(id: string) => navigate(ROUTES.MY_ACCOUNT_SUBSCRIPTIONS_USAGE(id))}
                      onViewBilling={(id: string) => navigate(ROUTES.MY_ACCOUNT_SUBSCRIPTIONS_BILLING(id))}
                      onCancel={handleCancelSubscription}
                      isCancelling={cancellingId === subscription.id}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Expired Subscriptions Grouped by Service */}
      {expiredServiceGroups.length > 0 && (
        <div className="space-y-8">
          <h2 className="text-2xl font-semibold text-gray-900">Expired Subscriptions</h2>
          {expiredServiceGroups.map((serviceGroup) => {
            const expiredSubscriptions = serviceGroup.subscriptions.filter(sub => sub.status !== 'active');
            return (
              <div key={serviceGroup.service.id} className="space-y-4">
                {/* Service Header */}
                <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                  {serviceGroup.service.icon_url && (
                    <img 
                      src={serviceGroup.service.icon_url} 
                      alt={`${serviceGroup.service.name} icon`}
                      className="w-8 h-8 rounded opacity-50"
                    />
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-500">{serviceGroup.service.name}</h3>
                    <p className="text-sm text-gray-400">{serviceGroup.service.description}</p>
                  </div>
                </div>
                
                {/* Service Subscriptions */}
                <div className="space-y-4 pl-4">
                  {expiredSubscriptions.map((subscription) => (
                    <SubscriptionCard
                      key={subscription.id}
                      subscription={subscription}
                      onResubscribe={(id: string) => console.log('Resubscribe:', id)}
                      variant="expired"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MySubscriptions;
